import { useAlertContext, useConfirmationDialogContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { PriorityRequestResponse, priorityRequestsApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { PriorityRequestRowActions } from "../PriorityRequests.types";
import { formatConsequences } from "../PriorityRequests.utils";

export function usePriorityRequestActions(): PriorityRequestRowActions {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.PriorityRequests] });
  };

  const invalidateWithClientHistory = (clientId: string) => {
    invalidate();
    void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientChangeInfo, clientId] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientChangeHistory, clientId] });
  };

  const pullForwardMutation = useMutation({
    mutationFn: (clientId: string) => priorityRequestsApi.pullPriorityRequestForward({ clientId }),
    onSuccess: (_, clientId) => {
      invalidateWithClientHistory(clientId);
      showSuccess(t("priorityRequests.pullForward.success"));
    },
  });

  const contactAttemptMutation = useMutation({
    mutationFn: (clientId: string) => priorityRequestsApi.recordPriorityRequestContactAttempt({ clientId }),
    onSuccess: (_, clientId) => {
      invalidateWithClientHistory(clientId);
      showSuccess(t("priorityRequests.contactAttempt.success"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (clientId: string) => priorityRequestsApi.deletePriorityRequest({ clientId }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("priorityRequests.delete.success"));
    },
  });

  const confirmPullForward = (row: PriorityRequestResponse) => {
    showConfirmationDialog({
      dialogTitleText: t("priorityRequests.pullForward.confirmTitle"),
      dialogText: formatConsequences(t("priorityRequests.pullForward.confirmText"), [
        t("priorityRequests.pullForward.consequences.release"),
        t("priorityRequests.pullForward.consequences.create"),
        t("priorityRequests.pullForward.consequences.assign"),
      ]),
      agreeText: t("priorityRequests.pullForward.confirm"),
      onClose: (confirmed) => {
        if (confirmed) {
          pullForwardMutation.mutate(row.clientId);
        }
      },
    });
  };

  const confirmContactAttempt = (row: PriorityRequestResponse) => {
    showConfirmationDialog({
      dialogTitleText: t("priorityRequests.contactAttempt.confirmTitle"),
      dialogText: t("priorityRequests.contactAttempt.confirmText"),
      agreeText: t("priorityRequests.contactAttempt.confirm"),
      onClose: (confirmed) => {
        if (confirmed) {
          contactAttemptMutation.mutate(row.clientId);
        }
      },
    });
  };

  const confirmDelete = (row: PriorityRequestResponse) => {
    showConfirmationDialog({
      dialogTitleText: t("priorityRequests.delete.confirmTitle"),
      dialogText: t("priorityRequests.delete.confirmText"),
      agreeText: t("priorityRequests.delete.confirm"),
      agreeColor: "error",
      onClose: (confirmed) => {
        if (confirmed) {
          deleteMutation.mutate(row.clientId);
        }
      },
    });
  };

  return {
    onPullForward: confirmPullForward,
    onContactAttempt: confirmContactAttempt,
    onDelete: confirmDelete,
    isBusy: pullForwardMutation.isPending || contactAttemptMutation.isPending || deleteMutation.isPending,
  };
}
